import { Minus, Plus } from 'lucide-react';
import type { MappingOp } from '../../lib/mappingEngine';
import type { SourcePath } from '../../lib/mappingSpecEdit';

// The "Number operations" rows of a mapping rule: an ordered chain like
//   ÷ 1000   − (quarter field)   + (fy field)
// run by the mapping engine (all three: Go, TypeScript, C) after cast and unit
// scale. Each row is an operator and an operand - a fixed number or another
// field of the same record - with (−) to remove it and (+) to add one after it.
// A legacy `unitScale` shows as the first row ("×") so nothing is hidden; it
// stays a plain unitScale unless the user removes it.

const OPERATORS: { value: string; label: string; name: string }[] = [
  { value: '/', label: '÷', name: 'divide by' },
  { value: '*', label: '×', name: 'multiply by' },
  { value: '+', label: '+', name: 'add' },
  { value: '-', label: '−', name: 'subtract' },
];

const NUMBER = '__number__';
const FIELD_PREFIX = 'field:';

const selectCls = 'rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-1 py-1 text-[12px] outline-none focus:border-blue-500';
const inputCls = 'rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-[12px] outline-none focus:border-blue-500 min-w-0';
const iconBtn = 'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-black/15 dark:border-white/15 hover:bg-black/[0.05] dark:hover:bg-white/[0.08]';

interface Props {
  label: string;
  ops: MappingOp[];
  unitScale?: number;
  paths: SourcePath[];
  onChange: (next: { ops: MappingOp[]; unitScale?: number }) => void;
}

export function OpsEditor({ label, ops, unitScale, paths, onChange }: Props) {
  const hasScale = typeof unitScale === 'number';
  // `scale` is passed explicitly (never defaulted): undefined means "remove the scale".
  const emitBoth = (nextOps: MappingOp[], nextScale: number | undefined) => onChange({ ops: nextOps, unitScale: nextScale });
  const emit = (nextOps: MappingOp[]) => emitBoth(nextOps, unitScale);
  const setOp = (i: number, patch: Partial<MappingOp>) => emit(ops.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const insertAfter = (i: number) => emit([...ops.slice(0, i + 1), { op: '+', value: 0 }, ...ops.slice(i + 1)]);
  const remove = (i: number) => emit(ops.filter((_, idx) => idx !== i));

  const operandValue = (o: MappingOp) => (typeof o.from === 'string' && o.from !== '' ? `${FIELD_PREFIX}${o.from}` : NUMBER);
  const pathOptions = (o: MappingOp) => (o.from && !paths.some((p) => p.path === o.from) ? [{ path: o.from, sample: '' }, ...paths] : paths);

  return (
    <div className="flex flex-col gap-1" role="group" aria-label={`Number operations for ${label}`}>
      <span className="text-[11px] font-medium text-black/50 dark:text-white/50">Number operations</span>
      {hasScale && (
        <div className="flex items-center gap-1">
          <span className="w-12 text-center text-[12px]" aria-hidden>
            ×
          </span>
          <input
            aria-label={`Scale for ${label}`}
            type="number"
            step="any"
            value={unitScale}
            onChange={(e) => emitBoth(ops, e.target.value === '' || Number.isNaN(Number(e.target.value)) ? undefined : Number(e.target.value))}
            className={`${inputCls} flex-1`}
          />
          <button type="button" aria-label={`Remove scale for ${label}`} onClick={() => emitBoth(ops, undefined)} className={iconBtn}>
            <Minus size={12} />
          </button>
        </div>
      )}
      {ops.map((o, i) => (
        <div key={i} className="flex items-center gap-1">
          <select aria-label={`Operator ${i + 1} for ${label}`} value={o.op} onChange={(e) => setOp(i, { op: e.target.value })} className={`${selectCls} w-12`}>
            {OPERATORS.map((p) => (
              <option key={p.value} value={p.value} title={p.name}>
                {p.label}
              </option>
            ))}
            {!OPERATORS.some((p) => p.value === o.op) && <option value={o.op}>{o.op}</option>}
          </select>
          <select
            aria-label={`Operand ${i + 1} for ${label}`}
            value={operandValue(o)}
            onChange={(e) => {
              const v = e.target.value;
              if (v === NUMBER) setOp(i, { from: undefined, value: o.value ?? 0 });
              else setOp(i, { from: v.slice(FIELD_PREFIX.length), value: undefined });
            }}
            className={`${selectCls} min-w-0 flex-1`}
          >
            <option value={NUMBER}>a number</option>
            {pathOptions(o).map((p) => (
              <option key={p.path} value={`${FIELD_PREFIX}${p.path}`}>
                field: {p.path}
                {p.sample ? ` · ${p.sample}` : ''}
              </option>
            ))}
          </select>
          {operandValue(o) === NUMBER && (
            <input
              aria-label={`Amount ${i + 1} for ${label}`}
              type="number"
              step="any"
              value={typeof o.value === 'number' ? o.value : ''}
              onChange={(e) => setOp(i, { value: e.target.value === '' || Number.isNaN(Number(e.target.value)) ? undefined : Number(e.target.value) })}
              className={`${inputCls} w-24`}
            />
          )}
          <button type="button" aria-label={`Remove operation ${i + 1} for ${label}`} onClick={() => remove(i)} className={iconBtn}>
            <Minus size={12} />
          </button>
          <button type="button" aria-label={`Add operation after ${i + 1} for ${label}`} onClick={() => insertAfter(i)} className={iconBtn}>
            <Plus size={12} />
          </button>
        </div>
      ))}
      {ops.length === 0 && (
        <button
          type="button"
          aria-label={`Add operation for ${label}`}
          onClick={() => emit([{ op: '/', value: 1000 }])}
          className="flex w-fit items-center gap-1 rounded-lg border border-black/15 dark:border-white/15 px-2 py-0.5 text-[12px] hover:bg-black/[0.05] dark:hover:bg-white/[0.08]"
        >
          <Plus size={12} /> Add operation
        </button>
      )}
      {(ops.length > 0 || hasScale) && (
        <span className="text-[11px] text-black/45 dark:text-white/45">
          Applied in order, after the cast. If the number or an operand field is missing or not a number, the value is left out rather than guessed.
        </span>
      )}
    </div>
  );
}
